import type { ReactNode } from 'react'

import { cn } from '../utilitarios/cn'
import { Girador } from './girador'

interface PropriedadesBase {
  titulo?: string
  descricao?: string
  acao?: ReactNode
  children?: ReactNode
  className?: string
}

function Moldura({
  icone,
  titulo,
  descricao,
  acao,
  children,
  className,
  tomIcone,
}: PropriedadesBase & { icone: ReactNode; tomIcone: string }) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <div className={cn('flex h-12 w-12 items-center justify-center rounded-full', tomIcone)}>
        {icone}
      </div>
      {titulo ? <h3 className="text-sm font-semibold text-conteudo">{titulo}</h3> : null}
      {descricao ? (
        <p className="max-w-md text-sm text-conteudo-suave">{descricao}</p>
      ) : null}
      {children}
      {acao ? <div className="mt-1">{acao}</div> : null}
    </div>
  )
}

/** Lista vazia — o caso normal, não um erro. Sem tom de alarme. */
export function EstadoVazio({
  titulo = 'Nada por aqui',
  descricao,
  acao,
  children,
  className,
}: PropriedadesBase) {
  return (
    <Moldura
      className={className}
      tomIcone="bg-superficie-3 text-conteudo-fraco"
      icone={
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M4 7h16M4 12h16M4 17h9" strokeLinecap="round" />
        </svg>
      }
      titulo={titulo}
      descricao={descricao}
      acao={acao}
    >
      {children}
    </Moldura>
  )
}

/** Falha de carregamento. Sempre com uma ação de "tentar novamente". */
export function EstadoErro({
  titulo = 'Não foi possível carregar',
  descricao,
  acao,
  children,
  className,
}: PropriedadesBase) {
  return (
    <Moldura
      className={className}
      tomIcone="bg-perigo-suave text-perigo"
      icone={
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M12 8v5m0 3.5h.01M10.3 3.9 2.6 17.2A1.9 1.9 0 0 0 4.3 20h15.4a1.9 1.9 0 0 0 1.7-2.8L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      }
      titulo={titulo}
      descricao={descricao}
      acao={acao}
    >
      {children}
    </Moldura>
  )
}

/**
 * Permissão negada.
 *
 * A tela **também** existe porque esconder botão não é autorizar (RNF-20): o
 * backend nega de todo modo, e quando nega o usuário precisa entender que o
 * problema é o papel dele, não um defeito.
 */
export function EstadoSemPermissao({
  titulo = 'Você não tem acesso a esta área',
  descricao = 'Seu perfil não permite ver este conteúdo. Se precisar dele para trabalhar, peça ao administrador.',
  acao,
  className,
}: PropriedadesBase) {
  return (
    <Moldura
      className={className}
      tomIcone="bg-atencao-suave text-atencao"
      icone={
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M7 11V8a5 5 0 0 1 10 0v3m-11 0h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      }
      titulo={titulo}
      descricao={descricao}
      acao={acao}
    />
  )
}

export function EstadoCarregando({
  descricao = 'Carregando…',
  className,
}: {
  descricao?: string
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-12', className)}>
      <Girador tamanho="lg" className="text-marca-500" rotulo={null} />
      <p className="text-sm text-conteudo-suave">{descricao}</p>
    </div>
  )
}
